import React, { useRef, useState } from 'react';
import { observer } from 'mobx-react';
import { Box, Button, ButtonBase, Dialog, DialogContent, Divider, IconButton, Skeleton, Stack, TextField, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useTheme } from '@mui/material/styles';
import type PurchaseStore from '../../store/purchase/purchaseStore';
import type { IPurchaseConfirmation } from '../../store/purchase/purchaseStore';
import WikiHelpIcon from '../../components/wiki/WikiHelpIcon';
import CompanyField from './CompanyField';
import { formatCount, priceLabel, savingLabel, unitPriceLabel } from './money';
import currency from '../../assets/images/curency.png';
import receipt from '../../assets/images/receipt.png';

const TitleID = 'purchase-dialog-title';

/** The wiki entry that explains credits, shown on the help icon beside the close button once an admin has written it. */
//const WIKI_TOOLTIP_ID = 'BuyCredits';
const WikiTooltipID = 'Export';

/** Under the invoice email a company keeps on file, in place of the field. */
export const INVOICE_EMAIL_ON_FILE_LINE = "The company's invoice email on file will be used.";

/** What an accepted purchase reads like: the credits added, grouped as the cards group them, and the balance they made. */
const creditsAddedLine = ({ credits, totalCreditsAfter }: IPurchaseConfirmation): string => (totalCreditsAfter === null ? `${formatCount(credits)} credits added.` : `${formatCount(credits)} credits added. Your balance is now ${formatCount(totalCreditsAfter)} credits.`);

/** Where the arrow keys take the selection in a radio group of `count` cards: the next or previous one, wrapping round, or the ends. */
const packageIndexAfterKey = (key: string, current: number, count: number): number | null => {
  switch (key) {
    case 'ArrowRight':
    case 'ArrowDown':
      return (current + 1) % count;
    case 'ArrowLeft':
    case 'ArrowUp':
      return (current - 1 + count) % count;
    case 'Home':
      return 0;
    case 'End':
      return count - 1;
    default:
      return null;
  }
};

/**
 * The purchase form: the quota packages on sale, one to pick, the company to invoice, and the
 * confirmation once the purchase stands.
 *
 * A thin observer. What is prefilled, what the company field resolves to and its caption shows, which
 * suggestions it offers, whether the form can be sent and what a refusal reads like are decided in the
 * purchase store; nothing here decides anything but layout and keyboard reach.
 *
 * Beside the form, a panel says what credits are for, over an illustration: on the left from the
 * tablet width up, a band above the form on a phone. Once a purchase stands the dialog is short, so
 * the panel shows a second illustration alone, drawn for that shape, and the help icon goes with the
 * form it explained.
 *
 * The packages are one radio group: one Tab stop, the arrow keys move the choice, and the chosen card
 * shows a focus ring when reached by keyboard. The dialog is named by its title, which says what the
 * form is for and, once a purchase stands, what became of it; what it says while loading and once a
 * purchase stands is announced.
 */
export default observer(function PurchaseDialog({ purchaseStore }: { purchaseStore: PurchaseStore }) {
  const theme = useTheme();
  const { canSubmit, confirmation, invoiceEmail, invoiceEmailError, invoiceEmailOnFile, invoiceEmailToSend, isLoading, isOpen, isSubmitting, loadFailureMessage, packages, purchaseFailureMessage, selectedPackage, selectedProductId } = purchaseStore;
  const cardRefs = useRef<Record<number, HTMLButtonElement | null>>({});
  // The email's error waits until the author has left the field, so it does not shout while they type.
  const [hasLeftInvoiceEmail, setHasLeftInvoiceEmail] = useState(false);

  if (!isOpen) return null;

  const close = () => purchaseStore.close();
  const showInvoiceEmailError = hasLeftInvoiceEmail && invoiceEmailError !== null;

  const selectedIndex = packages.findIndex((aPackage) => aPackage.productId === selectedProductId);

  const onPackageKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (isSubmitting || packages.length === 0) return;
    const next = packageIndexAfterKey(event.key, Math.max(selectedIndex, 0), packages.length);
    if (next === null) return;
    event.preventDefault();
    const aPackage = packages[next];
    purchaseStore.selectPackage(aPackage.productId);
    cardRefs.current[aPackage.productId]?.focus();
  };

  const body = () => {
    if (isLoading) {
      // The form's own shape while the packages are read: two package cards, the divider, the two fields.
      // So the dialog opens at the size it keeps, and nothing jumps when the packages arrive.
      return (
        <Stack spacing={1.5} role='status' aria-label='Loading the packages'>
          <Skeleton variant='rounded' height={72} sx={{ borderRadius: 2 }} />
          <Skeleton variant='rounded' height={72} sx={{ borderRadius: 2 }} />
          <Divider sx={{ my: 1.5 }} />
          <Skeleton variant='rounded' height={40} />
          <Skeleton variant='rounded' height={40} />
        </Stack>
      );
    }

    if (loadFailureMessage !== null) {
      return (
        <Typography variant='body2' color='error' role='alert'>
          {loadFailureMessage}
        </Typography>
      );
    }

    if (confirmation !== null) {
      return (
        <Stack spacing={1} data-testid='purchase-confirmation' role='status'>
          <Typography variant='body2'>{confirmation.isPending ? `${confirmation.productName}: ${confirmation.message}` : creditsAddedLine(confirmation)}</Typography>
          <Typography variant='body2' color='text.secondary'>
            The invoice will be sent to {invoiceEmailToSend}.
          </Typography>
        </Stack>
      );
    }

    return (
      <>
        {/* One row per package: the credits and what one costs on the left, the price on the right. */}
        <Stack role='radiogroup' aria-label='Package' spacing={1.5} onKeyDown={onPackageKeyDown}>
          {packages.map((aPackage, index) => {
            const isSelected = aPackage.productId === selectedProductId;
            const unitPrice = unitPriceLabel(aPackage);
            const saving = savingLabel(aPackage, packages);
            // One Tab stop for the group: the chosen card, or the first while none is chosen.
            const isTabStop = isSelected || (selectedIndex === -1 && index === 0);
            return (
              <ButtonBase
                key={aPackage.productId}
                ref={(element) => {
                  cardRefs.current[aPackage.productId] = element;
                }}
                role='radio'
                aria-checked={isSelected}
                tabIndex={isTabStop ? 0 : -1}
                disabled={isSubmitting}
                onClick={() => purchaseStore.selectPackage(aPackage.productId)}
                sx={{
                  display: 'flex',
                  width: '100%',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 2,
                  textAlign: 'left',
                  px: 2,
                  py: 1.5,
                  border: 2,
                  borderRadius: 2,
                  borderStyle: 'solid',
                  borderColor: isSelected ? 'primary.main' : theme.palette.action.disabledBackground,
                  background: isSelected ? theme.palette.bgThree.main : 'inherit',
                  '&:hover': { borderColor: isSelected ? 'primary.main' : 'text.secondary' },
                  '&.Mui-focusVisible': { outline: '2px solid', outlineColor: 'primary.main', outlineOffset: 2 },
                }}
              >
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant='subtitle1' component='div'>
                    {formatCount(aPackage.credits)} credits
                  </Typography>
                  {/* The value story the prices already hold: what a credit costs here, and how much less than in the dearest package. */}
                  {(unitPrice !== '' || saving !== null) && (
                    <Typography variant='caption' component='div' color='text.secondary'>
                      {unitPrice}
                      {unitPrice !== '' && saving !== null && ' · '}
                      {saving !== null && (
                        <Box component='span' sx={{ color: 'primary.main', fontWeight: 600 }}>
                          {saving}
                        </Box>
                      )}
                    </Typography>
                  )}
                </Box>
                <Typography variant='body2' component='div' color='text.secondary' sx={{ whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>
                  {priceLabel(aPackage)}
                </Typography>
              </ButtonBase>
            );
          })}
        </Stack>

        <Divider />

        <CompanyField field={purchaseStore.companyField} disabled={isSubmitting} />
        {invoiceEmailOnFile !== null ? (
          // A company with members keeps the address it has: shown, not editable.
          <TextField label='Invoice email' type='email' value={invoiceEmailOnFile} size='small' fullWidth disabled={isSubmitting} InputProps={{ readOnly: true }} helperText={INVOICE_EMAIL_ON_FILE_LINE} />
        ) : (
          <TextField
            label='Invoice email'
            type='email'
            autoComplete='email'
            value={invoiceEmail}
            onChange={(event) => purchaseStore.setInvoiceEmail(event.target.value)}
            onBlur={() => setHasLeftInvoiceEmail(true)}
            size='small'
            fullWidth
            disabled={isSubmitting}
            error={showInvoiceEmailError}
            helperText={showInvoiceEmailError ? invoiceEmailError : undefined}
          />
        )}

        {purchaseFailureMessage !== null && (
          <Typography variant='body2' color='error' role='alert'>
            {purchaseFailureMessage}
          </Typography>
        )}
      </>
    );
  };

  const isDone = confirmation !== null || loadFailureMessage !== null;
  const isConfirmed = confirmation !== null;

  return (
    <Dialog
      open
      // Escape and the backdrop close a dialog too, so a purchase in flight holds them as it holds the buttons.
      onClose={isSubmitting ? undefined : close}
      aria-labelledby={TitleID}
      fullWidth
      maxWidth='md'
      PaperProps={{
        sx: { borderRadius: 1, backgroundColor: theme.palette.bgTwo.main, color: theme.palette.bgTwo.contrastText, boxShadow: '0 0 15px 0px #000000' },
        elevation: 0,
      }}
    >
      <DialogContent sx={{ position: 'relative', p: 0, display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'clamp(220px, 42%, 380px) minmax(0, 1fr)' } }}>
        {/* The wiki's help for this form, the app's own way: a '?' whose entry shows on hover. Nothing shows until the entry exists. */}
        <Stack direction='row' alignItems='center' spacing={0.5} sx={{ position: 'absolute', top: 10, right: 10, zIndex: 2 }}>
          <IconButton aria-label='Close' onClick={close} disabled={isSubmitting}>
            <CloseIcon />
          </IconButton>
        </Stack>

        {/* What credits are for, beside the form. The headline leads; the illustration rises from the foot of the panel, and on a phone sits faintly behind the copy. */}
        <Box sx={{ position: 'relative', height: '100%', width: '100%', overflow: 'hidden', display: 'grid', gridTemplateRows: { sm: 'auto 1fr' }, color: theme.palette.bgZero.contrastText }}>
          <img
            src={isConfirmed ? receipt : currency}
            alt=''
            // A missing file leaves the panel with its copy alone; only the broken-image mark goes.
            onError={(event) => {
              event.currentTarget.style.visibility = 'hidden';
            }}
            style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', objectPosition: isConfirmed ? 'center' : 'center bottom' }}
          />
          {!isConfirmed && (
            <Stack spacing={1} sx={{ position: 'absolute', top: 0, left: 0, zIndex: 1, pt: { xs: 3, sm: 4.5 }, pb: { xs: 3, sm: 2 }, pl: { xs: 2.5, sm: 3.5 }, pr: { xs: 7, sm: 3.5 } }}>
              <Typography variant='h4' component='div' sx={{ textWrap: 'balance' }}>
                More power for your creativity
              </Typography>
              <Typography variant='body2' color='text.secondary' sx={{ maxWidth: '30ch' }}>
                Use credits to generate images, courses and more.
              </Typography>
            </Stack>
          )}
        </Box>

        <Stack spacing={3} data-testid='purchase-form' sx={{ minWidth: 0, px: { xs: 2.5, sm: 5 }, py: { xs: 3, sm: 4 } }}>
          <Stack direction='row' alignItems='end' spacing={2}>
            <Typography variant='h5' component='h2' id={TitleID}>
              {confirmation === null ? 'Buy credits' : confirmation.isPending ? 'Order registered' : 'Thank you for your purchase'}
            </Typography>

            {!isConfirmed && <WikiHelpIcon tooltipId={WikiTooltipID} tooltipPlacement='bottom' />}
          </Stack>

          {body()}

          <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 2, justifyContent: 'flex-end' }}>
            {isDone ? (
              <>
                {/* A form that could not be opened asks to be tried again; the way to do that is here, not back in the indicator. */}
                {loadFailureMessage !== null && (
                  <Button variant='newSecondaryText' onClick={() => void purchaseStore.open()}>
                    Try again
                  </Button>
                )}
                <Button variant='contained' onClick={close}>
                  Close
                </Button>
              </>
            ) : (
              <>
                {/* What is still to choose, beside the button that waits for it; the chosen package is named on the button itself. */}
                {!isLoading && selectedPackage === null && (
                  <Typography variant='body2' color='text.secondary' data-testid='purchase-summary' sx={{ flex: '1 1 auto' }}>
                    Choose a package to continue.
                  </Typography>
                )}
                {/* The quiet way out, in the theme's own text variant, so the one filled button is the one that buys. */}
                <Button variant='newSecondaryText' onClick={close} disabled={isSubmitting}>
                  Cancel
                </Button>
                {/* Nothing to buy until the packages are here, so no Buy button waits beside the skeletons. */}
                {!isLoading && (
                  <Button variant='contained' onClick={() => void purchaseStore.submit()} disabled={!canSubmit}>
                    {isSubmitting ? 'Buying...' : selectedPackage === null ? 'Buy' : `Buy ${formatCount(selectedPackage.credits)} credits`}
                  </Button>
                )}
              </>
            )}
          </Box>
        </Stack>
      </DialogContent>
    </Dialog>
  );
});
